import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_DRIVER_JOB, ROUTE_DRIVER_JOB_COMPLETED } from '../shared/routes';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ROUTE_DRIVER_DASHBOARD } from 'src/app/shared/routes';
import { CarrierDataService } from 'src/app/shared/services/carrier-data.service';
import { CompanyInterestService } from 'src/app/registration/shared/company-interest.service';
import { PublicCarrier } from 'src/app/shared/models/public-carrier';
import { StorageService } from 'src/app/shared/storage/storage.service';
import { DRIVER_TYPES_MAP, HOW_DID_YOU_HEAR_OPTIONS, KEY_DRIVER, OPTIONS_REQUIRING_DETAILS, POSITION_OPTIONS, RADIUS_OPTIONS_MAP } from '../shared/constants';
import { Action } from 'src/app/loads-shared/action';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/dialog/confitmation-dialog/confirmation-dialog.component';

@Component({
    selector: 'app-job-apply',
    templateUrl: './job-apply.component.html',
    styleUrls: ['./job-apply.component.scss'],
    standalone: false
})
export class JobApplyComponent implements OnInit {
  form: UntypedFormGroup;
  selectedPosition: string = null;
  selectedRadius: string = null;
  carrierCompanyId: string = null;
  carrierName: string = null;
  carrierData: PublicCarrier = null;
  availableRadiusOptions: { key: string, displayName: string }[] = [];
  driverType: string = null;
  positionOptions = POSITION_OPTIONS;
  howDidYouHearOptions: string[] = HOW_DID_YOU_HEAR_OPTIONS;
  optionsRequiringDetails: string[] = OPTIONS_REQUIRING_DETAILS;
  isSubmitting = false;

  constructor(
    private fb: UntypedFormBuilder,
    private readonly router: Router,
    private readonly carrierDataService: CarrierDataService,
    private readonly storageService: StorageService,
    private readonly dialog: MatDialog,
    private readonly companyInterestService: CompanyInterestService,
  ) { }

  ngOnInit(): void {
    this.form = this.createForm();
    this.carrierCompanyId = sessionStorage.getItem('carrierCompanyId');
    if (this.carrierCompanyId) {
      this.loadCarrierData(this.carrierCompanyId);
    }
  }

  loadCarrierData(companyId: string): void {
    this.carrierDataService.getCarrierPublicById(companyId).subscribe({
      next: (response) => {
        this.carrierData = response;
        this.setRadiusOptions(response.operationType);
      },
      error: (err) => {
        console.error('Error fetching carrier data', err);
      }
    });
  }

  private createForm(): UntypedFormGroup {
    return this.fb.group({
      position: ['', Validators.required],
      radius: ['', Validators.required],
      howDidYouHearAboutJob: ['', Validators.required],
      howDidYouHearAboutJobDescription: ['']
    });
  }

  get position() {
    return this.form.get('position');
  }

  get radius() {
    return this.form.get('radius');
  }

  getPositionErrorMessage() {
    return this.position.errors?.required ? 'Position is required' : '';
  }

  getRadiusErrorMessage() {
    return this.radius.errors?.required ? 'Radius of operation is required' : '';
  }

  markTouched(controlName: string): void {
    const control = this.form.get(controlName);
    control?.markAsTouched();

    if (controlName === 'howDidYouHearAboutJob') {
      const selected = control.value;
      const needsDetails = this.optionsRequiringDetails.includes(selected);
      const detailsCtrl = this.form.get('howDidYouHearAboutJobDescription');

      if (needsDetails) {
        if (selected === 'I am an existing employee of this company') {
          detailsCtrl?.clearValidators();
        } else {
          detailsCtrl?.setValidators([Validators.required]);
        }
      } else {
        detailsCtrl?.clearValidators();
        detailsCtrl?.setValue('');
      }

      detailsCtrl?.updateValueAndValidity();
    }
  }

  setRadiusOptions(operationType: string): void {
    const key = operationType?.toLowerCase();
    switch (key) {
      case 'provincial':
        this.availableRadiusOptions = RADIUS_OPTIONS_MAP.provincial;
        break;
      case 'acrosscanada':
        this.availableRadiusOptions = RADIUS_OPTIONS_MAP.acrossCanada;
        break;
      case 'canadaandus':
        this.availableRadiusOptions = RADIUS_OPTIONS_MAP.canadaAndUS;
        break;
      case 'intrastate':
        this.availableRadiusOptions = RADIUS_OPTIONS_MAP.intrastate;
        break;
      case 'acrossus':
        this.availableRadiusOptions = RADIUS_OPTIONS_MAP.acrossUS;
        break;
      case 'usandcanada':
        this.availableRadiusOptions = RADIUS_OPTIONS_MAP.usAndCanada;
        break;
      default:
        this.availableRadiusOptions = [];
    }
  }

  onSubmit(): void {
    if (!this.form.valid) return;

    this.isSubmitting = true;
    const model = this.prepareSaveModel();

    if (!model) {
      console.error('Failed to prepare payload');
      this.isSubmitting = false;
      return;
    }
    const companyId = this.carrierCompanyId;

    this.carrierDataService.createCarrierDriverAssociations(companyId, model).subscribe({
      next: () => {
        this.companyInterestService.removeCompanyInterest(companyId).subscribe({
          next: () => {
            this.companyInterestService.clearInviteApplySession();
            this.isSubmitting = false;
            this.router.navigate([`/${ROUTE_DRIVER_JOB}`, ROUTE_DRIVER_JOB_COMPLETED]);
          },
          error: () => {
            this.companyInterestService.clearInviteApplySession();
            this.isSubmitting = false;
            this.router.navigate([`/${ROUTE_DRIVER_JOB}`, ROUTE_DRIVER_JOB_COMPLETED]);
          }
        });
      },
      error: (err) => {
        console.error('Error submitting driver application', err);
        this.isSubmitting = false;
      }
    });
  }

  private prepareSaveModel(): any {
    if (!this.form || !this.form.value) {
      console.error('Form data is not available');
      return null;
    }

    const formModel = this.form.value;
    const driver = this.storageService.find(KEY_DRIVER);

    if (!driver || !this.carrierData) {
      console.error('Missing required driver or carrier data');
      return null;
    }

    const positionKey = formModel.position;
    const radiusKey = formModel.radius;

    const combinedKey = `${positionKey}${this.capitalizeFirstLetter(radiusKey)}`;

    if (!DRIVER_TYPES_MAP[combinedKey]) {
      console.error(`No matching driver type for key: ${combinedKey}`);
      return null;
    }

    return {
      id: driver.id,
      status: 'Prospect',
      email: driver.email,
      driverId: driver.driverID,
      companyDriverType: combinedKey,
      firstName: driver.firstName,
      lastName: driver.lastName,
      phoneNumber: driver.phoneNumber || '',
      address: driver?.addresses[0]?.completeAddress,
      howDidYouHearAboutJob: formModel.howDidYouHearAboutJob,
      howDidYouHearAboutJobDescription: formModel.howDidYouHearAboutJobDescription || ''
    };
  }

  private capitalizeFirstLetter(value: string): string {
    if (value === 'usAndCanada') return 'USAndCanada';
    if (value === 'acrossUS') return 'AcrossUS';
    if (value === 'intrastate') return 'Intrastate';
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  showWarningMessage(): void {
    const actions = this.createAcceptDialogActions();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Exit Application?',
        message: 'If you exit now, your application will not be submitted.',
        subMessage: 'You can return anytime by using the application link.',
        actions
      },
      disableClose: true
    });
    const confirmAction = actions[1];
    const cancelAction = actions[0];
    confirmAction.action = async () => this.navigateBackToProfile(dialogRef, confirmAction, cancelAction);
    cancelAction.action = () => this.dialogCancelAction(dialogRef, confirmAction, cancelAction);

    dialogRef.afterClosed().subscribe();
  }

  navigateBackToProfile(dialogRef, confirmAction, cancelAction): void { 
    confirmAction.disabled = true;
    cancelAction.disabled = true;
    this.companyInterestService.clearInviteApplySession();
    dialogRef.close();
    this.router.navigate([`/${ROUTE_DRIVER_DASHBOARD}`]);
  }
  private dialogCancelAction(dialogRef: MatDialogRef<any>, confirmAction: Action, cancelAction: Action): any {
    confirmAction.disabled = true;
    cancelAction.disabled = true;

    dialogRef.close();
  }
  
  private createAcceptDialogActions(): Action[] {
    const actions: Action[] = [];
    const NoAction = {
      name: 'Cancel',
      action: (data: any) => {},
      disabled: false
    } as Action;
    actions.push(NoAction);

    const OkayAction = {
      name: 'Proceed',
      action: (data: any) => {},
      disabled: false,
      color: 'primary'
    } as Action;
    actions.push(OkayAction);

    return actions;
  }
}
